import React from 'react';
import { Box, Card, IconButton } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { isNumber } from 'lodash/fp';
import { InventoryCountQuery } from '../../../../generated/graphql';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { useUser } from '../../../../utils/hooks/auth';
import {
    ColoredTwoLineCard,
    HighlightedInfoItemWrapper,
    HighlightedInfoWrapper,
    Typography,
} from '../../../../ui-kit';
import { usePriceService } from '../../../../utils/hooks/priceService';
import InventoryCountDetailsTable from './InventoryCountDetailsTable';
import StartEndCard from './StartEndCard';

interface InventoryCountDetailsTableProps {
    inventoryCount: InventoryCountQuery['inventoryCount'];
    onClose: () => void;
}

const InventoryCountDetailsView = ({ inventoryCount, onClose }: InventoryCountDetailsTableProps) => {
    const { t } = useTranslation();
    const { user: { defaultCurrency, cultureCode } } = useUser();
    const { dateTimeService } = useDateTimeService();
    const { priceService } = usePriceService();
    const {
        startDate, endDate,
        expectedValuation, realValuation,
        notCalculatedCounts, notMatchingCounts,
        shortageSurplus, deltas
    } = inventoryCount;
    const expectedValuationLocalized = priceService.toLocaleString(expectedValuation || 0, defaultCurrency.currencyCode);
    const realValuationLocalized = isNumber(realValuation)
        ? priceService.toLocaleString(realValuation, defaultCurrency.currencyCode)
        : t('Not finished');

    const startDateText = dateTimeService.localizeDate(startDate, cultureCode);
    const endDateText = endDate ? dateTimeService.localizeDate(endDate, cultureCode) : t('Not finished');

    const localizedShortageWithoutVAT = priceService.toLocaleString(shortageSurplus.shortage.withoutVat, defaultCurrency.currencyCode);
    const localizedSurplusWithoutVAT = priceService.toLocaleString(shortageSurplus.surplus.withoutVat, defaultCurrency.currencyCode);
    const localizedDeltaWithoutVAT = priceService.toLocaleString(deltas.withoutVat, defaultCurrency.currencyCode);

    return (
        <Card style={{ height: '100%', overflow: 'auto' }}>
            <Box px={3} py={2}>
                <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                    <ArrowBack />
                </IconButton>
                <Box mb={2} />
                <StartEndCard startDateText={startDateText} endDateText={endDateText} />
                <Box mb={2} />
                <HighlightedInfoWrapper>
                    <HighlightedInfoItemWrapper>
                        <ColoredTwoLineCard label={t('Expected worth')} value={expectedValuationLocalized} variant="primary" />
                    </HighlightedInfoItemWrapper>
                    <HighlightedInfoItemWrapper>
                        <ColoredTwoLineCard label={t('Real worth')} value={realValuationLocalized} variant="success" />
                    </HighlightedInfoItemWrapper>
                </HighlightedInfoWrapper>
                <HighlightedInfoWrapper>
                    <HighlightedInfoItemWrapper>
                        <ColoredTwoLineCard label={t('Surplus')} value={localizedSurplusWithoutVAT} variant="success" />
                    </HighlightedInfoItemWrapper>
                    <HighlightedInfoItemWrapper>
                        <ColoredTwoLineCard label={t('Shortage')} value={localizedShortageWithoutVAT} variant="error" />
                    </HighlightedInfoItemWrapper>
                    <HighlightedInfoItemWrapper>
                        <ColoredTwoLineCard label={t('Inventory balance')} value={localizedDeltaWithoutVAT} variant="primary" />
                    </HighlightedInfoItemWrapper>
                </HighlightedInfoWrapper>
                {notMatchingCounts ? (
                    <>
                        <Box mb={2.5} />
                        <Typography color="primary" variant="body2">{`${notMatchingCounts} ${t('Cards do not match')}`}</Typography>
                        <Box mb={2.5} />
                    </>
                ) : null}
                {notCalculatedCounts ? (
                    <>
                        <Box mb={2.5} />
                        <Typography color="primary" variant="body2">{`${notCalculatedCounts} ${t('Cards are not counted')}`}</Typography>
                        <Box mb={2.5} />
                    </>
                ) : null}
            </Box>
            <InventoryCountDetailsTable inventoryCountId={inventoryCount.id} />
        </Card>
    );
};

export default InventoryCountDetailsView;
